import { useCallback, useEffect, useState } from 'react';

import { Grid } from '@frontierag/frontier-ui-library';
import { DISPLAY_PRODUCT_SUBGROUP, OXBURY_CODE, PRODUCT_CUSTOM_PAYMENT_CODE } from 'appConstants';
import ProductSubgroup from 'components/ProductSubgroup';
import type { IProductSubgroup } from 'contexts/Products';
import { useField, useFormikContext } from 'formik';
import type { IGenericObject } from 'types';

import BagSize from '../ProductFormFields/BagSize';
import BulkQuantity from '../ProductFormFields/BulkQuantity';
import BusinessContact from '../ProductFormFields/BusinessContact';
import BusinessName from '../ProductFormFields/BusinessName';
import DeliveryAddress from '../ProductFormFields/DeliveryAddress';
import DeliveryEndDate from '../ProductFormFields/DeliveryEndDate';
import DeliveryStartDate from '../ProductFormFields/DeliveryStartDate';
import Notes from '../ProductFormFields/Notes';
import PaymentDue from '../ProductFormFields/PaymentDue';
import PaymentTerms from '../ProductFormFields/PaymentTerms';
import ProductName from '../ProductFormFields/ProductName';
import ProductPrice from '../ProductFormFields/ProductPrice';
import TransportMethod from '../ProductFormFields/TransportMethod';
import type { IProductFields } from './types';

export default function ProductForm({
  isEdit = false,
  actions,
  isDisabled,
  location,
  customerReference,
  isDebtOwner,
}: IProductFields) {
  const [{ value: paymentTermsCode }] = useField('paymentTerms.paymentTermsCode');
  const { setValues, values } = useFormikContext<IGenericObject>();

  const [{ value: internalOrderId }] = useField('internalOrderId');
  const [selectedSubGroup, setSelectedSubGroup] = useState<IProductSubgroup | null>(null);

  const handleSubGroupChange = useCallback(
    async (value) => {
      setSelectedSubGroup(value);

      // Reset product on subgroup change
      if (values.packagedItemReference || values.itemGroupRef) {
        await setValues(
          {
            ...values,
            productData: {},
            packagedItemReference: '',
            itemGroupRef: '',
          },
          true,
        );
      }
    },
    [setValues, values],
  );

  // Reset selected subgroup when product was added
  useEffect(() => {
    if (internalOrderId) {
      setSelectedSubGroup(null);
    }
  }, [internalOrderId]);

  return (
    <Grid direction="column" container rowSpacing={3} wrap="nowrap">
      {!isEdit && DISPLAY_PRODUCT_SUBGROUP && (
        <Grid item>
          <ProductSubgroup
            onChange={handleSubGroupChange}
            selectedSubgroup={selectedSubGroup}
            textFieldProps={{ name: 'productSubgroup' }}
          />
        </Grid>
      )}
      <Grid item>
        <ProductName
          disabled={isEdit || isDisabled}
          location={location}
          selectedSubGroupDescription={
            selectedSubGroup ? selectedSubGroup.subGroupDescription : null
          }
        />
      </Grid>
      <Grid item>
        <BagSize />
      </Grid>
      <Grid item container columnSpacing={2}>
        <Grid item xs={6}>
          <BulkQuantity disabled={isDisabled} />
        </Grid>
        <Grid item xs={6}>
          <ProductPrice disabled={isDisabled} />
        </Grid>
      </Grid>
      {!isDebtOwner && (
        <Grid item>
          <PaymentTerms disabled={isDisabled} />
        </Grid>
      )}
      {!isDebtOwner && paymentTermsCode === PRODUCT_CUSTOM_PAYMENT_CODE && (
        <Grid item>
          <PaymentDue disabled={isDisabled} />
        </Grid>
      )}
      {!isDebtOwner && paymentTermsCode && paymentTermsCode.startsWith(OXBURY_CODE) && (
        <>
          <Grid item>
            <BusinessName disabled={isDisabled} />
          </Grid>
          <Grid item>
            <BusinessContact disabled={isDisabled} />
          </Grid>
        </>
      )}
      <Grid item>
        <TransportMethod />
      </Grid>
      <Grid item container columnSpacing={2}>
        <Grid item xs={6}>
          <DeliveryStartDate />
        </Grid>
        <Grid item xs={6}>
          <DeliveryEndDate />
        </Grid>
      </Grid>
      <Grid item>
        <DeliveryAddress customerReference={customerReference} />
      </Grid>
      <Grid item>
        <Notes disabled={isDisabled} />
      </Grid>
      {actions && <Grid item>{actions}</Grid>}
    </Grid>
  );
}
